import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Row, Col } from 'react-bootstrap'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  FaTruck, FaUserTie, FaClipboardList, FaFileInvoice,
  FaWarehouse, FaUsers, FaArrowRight, FaCheckCircle,
  FaBuilding, FaEnvelope, FaPhone, FaShieldAlt,
  FaTachometerAlt, FaFileContract, FaHandshake,
  FaTimesCircle, FaHourglassHalf, FaChartBar,
} from 'react-icons/fa'
import { fetchMyRequests } from '../../store/slices/transportRequestSlice'
import { fetchTrucks } from '../../store/slices/truckSlice'
import { fetchDrivers } from '../../store/slices/driverSlice'
import { fetchEntities } from '../../store/slices/entitySlice'
import { fetchUsers } from '../../store/slices/userSlice'
import '../../assets/scss/pages/Dashboard.scss'

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.35, ease: 'easeOut' } }),
}

const Dashboard = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector(state => state.auth)
  const { trucks = [] }   = useSelector(state => state.truck)
  const { drivers = [] }  = useSelector(state => state.driver)
  const { entities = [] } = useSelector(state => state.entity)
  const { users = [] }    = useSelector(state => state.user)
  const { requests = [] } = useSelector(state => state.transportRequest)

  const [greeting, setGreeting] = useState('Good morning')

  useEffect(() => {
    const h = new Date().getHours()
    if (h < 12) setGreeting('Good morning')
    else if (h < 17) setGreeting('Good afternoon')
    else setGreeting('Good evening')
  }, [])

  useEffect(() => {
    dispatch(fetchTrucks({ page: 1, limit: 1000 }))
    dispatch(fetchDrivers({ page: 1, limit: 1000 }))
    dispatch(fetchEntities({ page: 1, limit: 1000 }))
    dispatch(fetchUsers({ page: 1, limit: 1000 }))
    dispatch(fetchMyRequests({ page: 1, limit: 1000 }))
  }, [dispatch])

  // ── Derived stats ────────────────────────────────────────────────────────
  const activeTrucks    = trucks.filter(t => t.isActive).length
  const inactiveTrucks  = trucks.length - activeTrucks
  const activeDrivers   = drivers.filter(d => d.isActive).length

  const pendingReqs    = requests.filter(r => ['invited','viewed'].includes(r.transporter_status)).length
  const acceptedReqs   = requests.filter(r => r.transporter_status === 'accepted').length
  const completedReqs  = requests.filter(r => r.transporter_status === 'completed').length
  const rejectedReqs   = requests.filter(r => r.transporter_status === 'rejected').length
  const inProgressReqs = requests.filter(r => r.transporter_status === 'in_progress').length

  const proposalsSent  = requests.filter(r => r.my_proposal_status).length
  const nominationsDone = requests.filter(r => r.nomination_status === 'nominated').length

  const kpis = [
    { label: 'Trucks',       value: trucks.length,    note: `${activeTrucks} active`,     icon: <FaTruck />,         path: '/truck'             },
    { label: 'Drivers',      value: drivers.length,   note: `${activeDrivers} on duty`,   icon: <FaUserTie />,       path: '/driver'            },
    { label: 'Requests',     value: requests.length,  note: `${pendingReqs} pending`,     icon: <FaClipboardList />, path: '/transport-request' },
    { label: 'Proposals',    value: proposalsSent,    note: 'submitted by you',           icon: <FaFileContract />,  path: '/freight-proposal'  },
    { label: 'Nominations',  value: nominationsDone,  note: 'trucks nominated',           icon: <FaHandshake />,     path: '/truck-nomination'  },
    { label: 'Entities',     value: entities.length,  note: 'registered partners',        icon: <FaWarehouse />,     path: '/entity'            },
    { label: 'Team Members', value: users.length,     note: 'in your company',            icon: <FaUsers />,         path: '/user'              },
    { label: 'Bilties',      value: completedReqs,    note: 'completed trips',            icon: <FaFileInvoice />,   path: '/bilty'             },
  ]

  const dateStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  const reqStatusData = [
    { label: 'Pending',     count: pendingReqs,    color: '#f59e0b', icon: <FaHourglassHalf /> },
    { label: 'Accepted',    count: acceptedReqs,   color: '#10b981', icon: <FaCheckCircle /> },
    { label: 'In Progress', count: inProgressReqs, color: '#3b82f6', icon: <FaChartBar /> },
    { label: 'Completed',   count: completedReqs,  color: '#8b5cf6', icon: <FaFileInvoice /> },
    { label: 'Rejected',    count: rejectedReqs,   color: '#ef4444', icon: <FaTimesCircle /> },
  ]

  return (
    <div className="db-page">

      {/* ── HEADER ── */}
      <motion.div custom={0} variants={fade} initial="hidden" animate="visible" className="db-header">
        <div className="db-header-left">
          <div className="db-header-icon"><FaTachometerAlt /></div>
          <div>
            <h1 className="db-title">{greeting}, <span>{user?.name || user?.firstName || 'Admin'}</span></h1>
            <p className="db-sub">{dateStr}</p>
          </div>
        </div>
        <div className="db-company-badge">
          <FaBuilding className="db-co-icon" />
          <span>{user?.company?.name || 'TradeSetu'}</span>
        </div>
      </motion.div>

      {/* ── KPI CARDS ── */}
      <Row className="g-3 mb-3">
        {kpis.map((k, i) => (
          <Col key={k.label} xs={6} sm={4} md={3} xl={3}>
            <motion.div
              custom={i + 1} variants={fade} initial="hidden" animate="visible"
              className="db-kpi"
              onClick={() => navigate(k.path)}
              style={{ cursor: 'pointer' }}
            >
              <div className="db-kpi-icon">{k.icon}</div>
              <div className="db-kpi-val">{k.value}</div>
              <div className="db-kpi-label">{k.label}</div>
              <div className="db-kpi-note">{k.note}</div>
              <div className="db-kpi-arrow"><FaArrowRight /></div>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* ── ANALYTICS ROW ── */}
      <Row className="g-3">

        {/* Request Status breakdown */}
        <Col md={5}>
          <motion.div custom={10} variants={fade} initial="hidden" animate="visible" className="db-card">
            <div className="db-card-head">
              <span className="db-card-title">Request Status</span>
              <button className="db-link" onClick={() => navigate('/transport-request')}>View all <FaArrowRight /></button>
            </div>
            <div className="db-status-list">
              {reqStatusData.map(s => (
                <div key={s.label} className="db-status-row">
                  <span className="db-status-dot" style={{ background: s.color }} />
                  <span className="db-status-label">{s.label}</span>
                  <div className="db-status-bar-wrap">
                    <motion.div
                      className="db-status-bar"
                      style={{ background: s.color }}
                      initial={{ width: 0 }}
                      animate={{ width: requests.length > 0 ? `${Math.max((s.count / requests.length) * 100, s.count > 0 ? 5 : 0)}%` : '0%' }}
                      transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
                    />
                  </div>
                  <span className="db-status-count">{s.count}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Col>

        {/* Fleet Overview */}
        <Col md={4}>
          <motion.div custom={11} variants={fade} initial="hidden" animate="visible" className="db-card">
            <div className="db-card-head">
              <span className="db-card-title">Fleet Overview</span>
              <button className="db-link" onClick={() => navigate('/truck')}>View <FaArrowRight /></button>
            </div>
            <div className="db-fleet-rows">
              {[
                { label: 'Total Trucks',    val: trucks.length,   head: true },
                { label: 'Active',          val: activeTrucks },
                { label: 'Inactive',        val: inactiveTrucks },
                { label: 'Total Drivers',   val: drivers.length,  head: true },
                { label: 'On Duty',         val: activeDrivers },
                { label: 'Off Duty',        val: drivers.length - activeDrivers },
              ].map(row => (
                <div key={row.label} className={`db-fleet-row${row.head ? ' db-fleet-row--head' : ''}`}>
                  <span className="db-fleet-label">{row.label}</span>
                  <span className="db-fleet-val">{row.val}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Col>

        {/* Company Info */}
        <Col md={3}>
          <motion.div custom={12} variants={fade} initial="hidden" animate="visible" className="db-card">
            <div className="db-card-head">
              <span className="db-card-title">Company</span>
              <button className="db-link" onClick={() => navigate('/company/profile')}>Edit <FaArrowRight /></button>
            </div>
            <div className="db-company-avatar">
              {(user?.company?.name || 'T').charAt(0).toUpperCase()}
            </div>
            <div className="db-company-name">{user?.company?.name || '—'}</div>
            <div className="db-company-type">{user?.company?.companyType || 'Transport Company'}</div>
            <div className="db-info-rows">
              {[
                { icon: <FaEnvelope />,  val: user?.company?.email },
                { icon: <FaPhone />,     val: user?.company?.phone },
                { icon: <FaShieldAlt />, val: user?.role },
                { icon: <FaUsers />,     val: `${users.length} team member${users.length !== 1 ? 's' : ''}` },
              ].map((row, i) => (
                <div key={i} className="db-info-row">
                  <span className="db-info-icon">{row.icon}</span>
                  <span className="db-info-val">{row.val || '—'}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Col>

      </Row>
    </div>
  )
}

export default Dashboard
